import { Plugin } from 'vite';
import * as path from 'path';
import fs from 'fs-extra';

// Helper to only write files when content changes to avoid unnecessary HMR triggers
async function writeFileIfChanged(filePath: string, content: string): Promise<boolean> {
	try {
		const exists = await fs.pathExists(filePath);
		if (exists) {
			const existingContent = await fs.readFile(filePath, 'utf-8');
			if (existingContent === content) {
				return false; // No change needed
			}
		}
		await fs.writeFile(filePath, content, 'utf-8');
		return true; // File was written
	} catch (error) {
		// If there's an error reading, just write the file
		await fs.writeFile(filePath, content, 'utf-8');
		return true;
	}
}

export interface PugConfig {
	enabled?: boolean;
	options?: {
		pretty?: boolean;
		filters?: Record<string, any>;
		globals?: Record<string, any>;
		cache?: boolean;
		inlineRuntimeFunctions?: boolean;
		compileDebug?: boolean;
	};
}

export function createPugPlugin(cwd: string, config: PugConfig = {}): Plugin {
	const pugOptions = {
		pretty: true,
		cache: false,
		compileDebug: true,
		...config.options
	};

	return {
		name: 'frame-pug',

		configResolved(resolvedConfig) {
			// Only add Pug configuration if enabled
			if (config.enabled !== false) {
				console.log('[Frame Pug] Pug templating enabled with options:', pugOptions);
			}
		},

		async buildStart() {
			// Generate Pug-specific development helpers
			if (config.enabled !== false) {
				await generatePugHelpers(cwd, pugOptions);
			}
		},

		configureServer(server) {
			// Watch for Pug configuration changes
			const configFiles = ['frame.config.js', 'frame.config.mjs', 'frame.config.ts'];

		configFiles.forEach(configFile => {
			const configPath = path.join(cwd, configFile);
			if (fs.existsSync(configPath)) {
				server.watcher.add(configPath);
			}
		});

		server.watcher.on('change', async (file) => {
			if (configFiles.some(cf => file.endsWith(cf))) {
				console.log('[Frame Pug] Configuration changed, regenerating Pug helpers...');

				// Reload the configuration from the file
				const { ConfigLoader } = await import('../config/ConfigLoader.js');
				const configLoader = new ConfigLoader(cwd);
				const frameConfig = await configLoader.load();
				const updatedConfig = frameConfig.templates?.pug || {};

				// Merge with defaults
				const updatedPugOptions = {
					pretty: true,
					cache: false,
					compileDebug: true,
					...updatedConfig.options
				};

				// Regenerate helpers with updated config
				await generatePugHelpers(cwd, updatedPugOptions);
			}
		});
	}
	};
}

async function generatePugHelpers(cwd: string, pugOptions: any): Promise<void> {
	const frameDir = path.join(cwd, 'src', '.frame');
	await fs.ensureDir(frameDir);

	const pugHelpersContent = `// Auto-generated Pug helpers - do not edit manually
export const pugConfig = ${JSON.stringify(pugOptions, null, 2)};

/**
 * Pug utility functions for Frame applications
 */
export const pugUtils = {
	/**
	 * Convert HTML attributes to Pug format
	 */
	attributesToPug(attributes: Record<string, string>): string {
		return Object.entries(attributes)
			.map(([key, value]) => \`\${key}="\${value}"\`)
			.join(' ');
	},

	/**
	 * Generate Pug class syntax from array
	 */
	classesToPug(classes: string[]): string {
		return classes.map(cls => \`.\${cls}\`).join('');
	},

	/**
	 * Check if current template is using Pug
	 */
	isPugTemplate(template: string): boolean {
		return template.includes('lang="pug"') || template.includes("lang='pug'");
	}
};

/**
 * Pug mixins for common Frame patterns
 */
export const pugMixins = \`
//- Frame utility mixins
mixin frame-button(text, onClick)
  button(class="frame-button" @click=onClick)
    if block
      block
    else
      = text

mixin frame-nav-link(to, text)
  router-link(to=to class="frame-nav-link")
    if block
      block
    else
      = text

mixin frame-card(title)
  .frame-card
    .frame-card-header
      h3.frame-card-title= title
    .frame-card-body
      if block
        block

mixin frame-container(size)
  .frame-container(class=size ? \`frame-container-\${size}\` : '')
    if block
      block
\`;

/**
 * Get Pug configuration for development
 */
export function getDevPugConfig() {
	return {
		...pugConfig,
		compileDebug: true,
		cache: false,
		pretty: true
	};
}

/**
 * Get Pug configuration for production
 */
export function getProdPugConfig() {
	return {
		...pugConfig,
		compileDebug: false,
		cache: true,
		pretty: false
	};
}

/**
 * Template conversion utilities
 */
export const templateUtils = {
	/**
	 * Convert simple HTML to Pug (basic conversion)
	 */
	htmlToPug(html: string): string {
		// This is a basic converter - for complex templates, manual conversion is recommended
		return html
			.replace(/<div>/g, 'div')
			.replace(/<\/div>/g, '')
			.replace(/<p>/g, 'p ')
			.replace(/<\/p>/g, '')
			.replace(/<h1>/g, 'h1 ')
			.replace(/<\/h1>/g, '')
			.replace(/<h2>/g, 'h2 ')
			.replace(/<\/h2>/g, '')
			.replace(/<h3>/g, 'h3 ')
			.replace(/<\/h3>/g, '')
			.replace(/<span>/g, 'span ')
			.replace(/<\/span>/g, '');
	},

	/**
	 * Extract class names from HTML for Pug conversion
	 */
	extractClasses(html: string): string[] {
		const classRegex = /class="([^"]+)"/g;
		const classes: string[] = [];
		let match;
		
		while ((match = classRegex.exec(html)) !== null) {
			classes.push(...match[1].split(' '));
		}
		
		return [...new Set(classes)];
	}
};
`;

	const pugHelpersPath = path.join(frameDir, 'pug-helpers.ts');
	const wasWritten = await writeFileIfChanged(pugHelpersPath, pugHelpersContent);

	if (wasWritten) {
		console.log('[Frame Pug] Pug helpers generated at:', pugHelpersPath);
	}
}

/**
 * Create Pug plugin with sensible defaults
 */
export function createDefaultPugPlugin(cwd: string): Plugin {
	return createPugPlugin(cwd, {
		enabled: true,
		options: {
			pretty: true,
			cache: false,
			compileDebug: true,
			inlineRuntimeFunctions: false
		}
	});
} 